import { ColumnInformationDto } from "components/annotations/interface";
import { assignColumnIdsToCells } from "./assignColumnIdsToCells";
import { generateColumnIds } from "./generateColumnIds";

/**
 * Ensures that a table body has columnIds assigned to all cells and columnInformation populated.
 * This function:
 * 1. Generates columnIds from the first row of the table
 * 2. Checks if columnIds already exist on cells
 * 3. If not, assigns columnIds to all header and body cells
 * 4. Ensures columnInformation array is present and matches the columnIds
 *
 * @param body - Table body object containing headerData and bodyData
 * @returns The updated body with column IDs and column information
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const addColumnInformation = (body: any): any => {
  // Generate columnIds from the body
  const columnIds = generateColumnIds(body);

  // If no columns detected, return body with empty columnInformation
  if (columnIds.length === 0) {
    return {
      ...body,
      columnInformation: [],
    };
  }

  // Only generate columnInformation if empty or mismatched
  const columnInformation =
    body.columnInformation &&
    body.columnInformation.length > 0 &&
    columnIds.length === body.columnInformation.length
      ? body.columnInformation
      : columnIds.map((columnId, index): ColumnInformationDto => ({
          columnId,
          originalColumnIndex: index,
        }));

  // Check if columnIds already exist on the cells
  const hasExistingColumnIds =
    (body.headerData &&
      typeof body.headerData === "object" &&
      "rows" in body.headerData &&
      Array.isArray(body.headerData.rows) &&
      body.headerData.rows.length > 0 &&
      "cells" in body.headerData.rows[0] &&
      Array.isArray(body.headerData.rows[0].cells) &&
      body.headerData.rows[0].cells.every(
        (cell: { columnId?: string }) => cell.columnId && cell.columnId !== "",
      )) ||
    (body.bodyData &&
      Array.isArray(body.bodyData) &&
      body.bodyData.length > 0 &&
      body.bodyData[0].rows.length > 0 &&
      "cells" in body.bodyData[0].rows[0] &&
      Array.isArray(body.bodyData[0].rows[0].cells) &&
      body.bodyData[0].rows[0].cells.every(
        (cell: { columnId?: string }) => cell.columnId && cell.columnId !== "",
      ));

  // If columnIds don't exist, assign them to cells
  if (!hasExistingColumnIds) {
    const bodyWithColumnIds = { ...body };

    // Assign columnIds to header and body cells
    assignColumnIdsToCells(bodyWithColumnIds, columnIds);

    return {
      ...bodyWithColumnIds,
      columnInformation,
    };
  }

  // ColumnIds exist, just ensure columnInformation is present
  return {
    ...body,
    columnInformation,
  };
};
